import React, { useState, useRef, useEffect } from 'react';
import { Settings, Palette, Check, Plus, Trash2, RotateCcw, Sun, Moon, Sparkles, Monitor, Apple, Image as ImageIcon, Leaf, Building2, Mountain, Upload, Link, Globe, Lock, Heart, Download, Users, Eye, EyeOff, LayoutGrid, Contact, Map, Calendar, MessageCircle, GripVertical, ChevronDown, ChevronRight, Folder, FolderOpen, PartyPopper, X, Edit2, Pin, Save, Briefcase, HeadphonesIcon, Coins, Package } from 'lucide-react';
import Button from '../ui/Button';
import { getModules, getPeopleModules, createModuleGroup, updateModuleGroup, deleteModuleGroup, addModuleToGroup, removeModuleFromGroup, MODULE_NAV_MAP } from '../../services/modulesService';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { getThemeName, createTheme, deleteTheme, uploadThemeImage, likeTheme, getCommunityThemes, getMyThemes } from '../../services/themesService';
import { MODULE_ID_MAP } from '../../hooks/useModules';

/**
 * Preferences page - Theme selection and customization
 */
const Preferences = () => {
    const { t, language, languages, visibleLanguageCodes, setVisibleLanguageCodes } = useLanguage();
    const { currentTheme, themes, changeTheme, applyCustomTheme, resetToDefault, refreshThemes } = useTheme();

    const [mainTab, setMainTab] = useState(() => {
        return localStorage.getItem('cabazaPreferencesMainTab') || 'themes';
    }); // themes, modules, homescreen
    const [themeSubTab, setThemeSubTab] = useState(() => {
        return localStorage.getItem('cabazaPreferencesThemeSubTab') || 'cabaza';
    }); // cabaza, community, mythemes, custom

    // Save tabs to localStorage when they change
    useEffect(() => {
        localStorage.setItem('cabazaPreferencesMainTab', mainTab);
    }, [mainTab]);

    useEffect(() => {
        localStorage.setItem('cabazaPreferencesThemeSubTab', themeSubTab);
    }, [themeSubTab]);
    const [selectedThemeId, setSelectedThemeId] = useState(currentTheme?.id || 0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [communityThemes, setCommunityThemes] = useState([]);
    const [myCustomThemes, setMyCustomThemes] = useState([]);
    const [loadingCommunity, setLoadingCommunity] = useState(false);

    // Custom theme state
    const [customColors, setCustomColors] = useState({
        primaryColor: currentTheme?.primaryColor || '#8b5cf6',
        secondaryColor: currentTheme?.secondaryColor || '#ec4899',
        backgroundColor: currentTheme?.backgroundColor || '#ffffff',
        surfaceColor: currentTheme?.surfaceColor || '#f9fafb',
        textPrimary: currentTheme?.textPrimary || '#111827',
        textSecondary: currentTheme?.textSecondary || '#6b7280',
        borderColor: currentTheme?.borderColor || '#e5e7eb'
    });
    const [customThemeName, setCustomThemeName] = useState('');
    const [customThemeNameFr, setCustomThemeNameFr] = useState('');
    const [customThemeNameEn, setCustomThemeNameEn] = useState('');

    // Image options
    const [imageSource, setImageSource] = useState('none'); // none, upload, url
    const [imageUrl, setImageUrl] = useState('');
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [overlayColor, setOverlayColor] = useState('rgba(0,0,0,0.4)');

    // Visibility
    const [visibility, setVisibility] = useState('private');

    // Modules visibility (stored in localStorage)
    // Default all modules to true, then merge with stored preferences
    const [modules, setModules] = useState(() => {
        const defaultModules = {
            contacts: true,
            messages: true,
            map: true,
            agenda: true,
            events: true
        };
        const stored = localStorage.getItem('cabazaModules');
        if (stored) {
            // Merge stored with defaults to ensure new modules are enabled by default
            return { ...defaultModules, ...JSON.parse(stored) };
        }
        return defaultModules;
    });

    // Modules from API
    const [allModules, setAllModules] = useState([]);
    const [moduleGroups, setModuleGroups] = useState([]);
    const [loadingModules, setLoadingModules] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#8b5cf6');
    const [editingGroup, setEditingGroup] = useState(null);
    const [draggedModule, setDraggedModule] = useState(null);
    const [dragOverGroup, setDragOverGroup] = useState(null);

    const fileInputRef = useRef(null);
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Load community themes when tab changes
    useEffect(() => {
        if (mainTab === 'themes' && (themeSubTab === 'community' || themeSubTab === 'mythemes')) {
            loadCommunityThemes();
        }
    }, [mainTab, themeSubTab]);

    // Load modules when modules tab is active
    useEffect(() => {
        if (mainTab === 'modules') {
            loadModulesData();
        }
    }, [mainTab]);

    // Listen for modules/groups changes from other components (sidebar, Modules page)
    useEffect(() => {
        const handleModulesGroupsChange = () => {
            if (mainTab === 'modules') {
                loadModulesData();
            }
        };
        window.addEventListener('modulesGroupsChanged', handleModulesGroupsChange);
        return () => window.removeEventListener('modulesGroupsChanged', handleModulesGroupsChange);
    }, [mainTab]);

    // Helper to normalize object keys to lowercase
    const normalizeKeys = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(normalizeKeys);
        return Object.keys(obj).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();
            acc[lowerKey] = normalizeKeys(obj[key]);
            return acc;
        }, {});
    };

    const loadModulesData = async () => {
        setLoadingModules(true);
        try {
            const [modulesData, groupsData] = await Promise.all([
                getModules(language),
                getPeopleModules(userId, language)
            ]);
            // Normalize keys to lowercase (API returns uppercase)
            const normalizedGroups = normalizeKeys(groupsData);
            setAllModules(normalizeKeys(modulesData));
            setModuleGroups(normalizedGroups);
            // Update localStorage for sidebar
            localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));
            // Note: Don't dispatch 'modulesGroupsChanged' here to avoid infinite loop
            // Only dispatch when actual changes are made (create, update, delete)
        } catch (error) {
            console.error('Error loading modules:', error);
        } finally {
            setLoadingModules(false);
        }
    };

    // Get modules not in any group
    const getUngroupedModules = () => {
        const groupedModuleIds = new Set();
        moduleGroups.forEach(group => {
            group.modules?.forEach(m => groupedModuleIds.add(m.moduleid));
        });
        return allModules.filter(m => !groupedModuleIds.has(m.id));
    };

    // Handle create group
    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        const result = await createModuleGroup(userId, newGroupName.trim(), newGroupColor);
        const success = result.success ?? result.SUCCESS;
        const message = result.message ?? result.MESSAGE;
        if (success) {
            setNewGroupName('');
            setNewGroupColor('#8b5cf6');
            await loadModulesData();
            setMessage({ type: 'success', text: t.preferences?.groupCreated || 'Group created' });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        } else {
            setMessage({ type: 'error', text: message });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        }
    };

    // Handle update group
    const handleUpdateGroup = async (groupId, data) => {
        const result = await updateModuleGroup(groupId, data);
        const success = result.success ?? result.SUCCESS;
        if (success) {
            await loadModulesData();
            setEditingGroup(null);
        }
    };

    // Handle delete group
    const handleDeleteGroup = async (groupId) => {
        const result = await deleteModuleGroup(groupId);
        const success = result.success ?? result.SUCCESS;
        if (success) {
            await loadModulesData();
            setMessage({ type: 'success', text: t.preferences?.groupDeleted || 'Group deleted' });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        }
    };

    // Handle add module to group
    const handleAddModuleToGroup = async (groupId, moduleId) => {
        const result = await addModuleToGroup(groupId, moduleId);
        const success = result.success ?? result.SUCCESS;
        const message = result.message ?? result.MESSAGE;
        if (success) {
            // Remove module from pinnedItems if it was pinned individually
            // Use same key as Modules.jsx: cabazaPinnedItems
            const storedPinned = localStorage.getItem('cabazaPinnedItems');
            if (storedPinned) {
                const pinnedItems = JSON.parse(storedPinned);
                // Find the module code to determine the module id for pinnedItems
                const module = allModules.find(m => m.id === moduleId);
                if (module) {
                    const moduleCode = MODULE_NAV_MAP[module.code] || module.code.toLowerCase();
                    const filtered = pinnedItems.filter(item =>
                        !(item.type === 'module' && item.id === moduleCode)
                    );
                    if (filtered.length !== pinnedItems.length) {
                        localStorage.setItem('cabazaPinnedItems', JSON.stringify(filtered));
                        // Notify sidebar
                        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
                    }
                }
            }
            await loadModulesData();
        } else {
            setMessage({ type: 'error', text: message });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        }
    };

    // Handle remove module from group
    const handleRemoveModuleFromGroup = async (groupId, moduleId) => {
        const result = await removeModuleFromGroup(groupId, moduleId);
        const success = result.success ?? result.SUCCESS;
        if (success) {
            await loadModulesData();
        }
    };

    // Check if a group is pinned (use same key as Modules.jsx: cabazaPinnedItems)
    const isGroupPinned = (groupId) => {
        const storedPinned = localStorage.getItem('cabazaPinnedItems');
        if (!storedPinned) return false;
        const pinnedItems = JSON.parse(storedPinned);
        return pinnedItems.some(item => item.type === 'group' && Number(item.id) === Number(groupId));
    };

    // Toggle pin group (use same key as Modules.jsx: cabazaPinnedItems)
    const handleTogglePinGroup = (groupId) => {
        const storedPinned = localStorage.getItem('cabazaPinnedItems');
        let pinnedItems = storedPinned ? JSON.parse(storedPinned) : [];

        const isPinned = pinnedItems.some(item => item.type === 'group' && Number(item.id) === Number(groupId));

        if (isPinned) {
            pinnedItems = pinnedItems.filter(item => !(item.type === 'group' && Number(item.id) === Number(groupId)));
        } else {
            pinnedItems.push({ type: 'group', id: groupId });
        }

        localStorage.setItem('cabazaPinnedItems', JSON.stringify(pinnedItems));
        // Notify sidebar
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        // Force re-render
        setModuleGroups([...moduleGroups]);
    };

    // Drag handlers for modules
    const handleModuleDragStart = (e, module, fromGroupId = null) => {
        setDraggedModule({ ...module, fromGroupId });
        // Also set dataTransfer for cross-component drag (to sidebar)
        const moduleCode = module.code || module.CODE;
        const moduleId = module.id || module.moduleid || module.MODULEID;
        e.dataTransfer.setData('moduleCode', moduleCode);
        e.dataTransfer.setData('sourceGroupId', fromGroupId ? fromGroupId.toString() : '');
        e.dataTransfer.setData('groupId', fromGroupId ? fromGroupId.toString() : '');
        e.dataTransfer.setData('moduleId', moduleId ? moduleId.toString() : '');
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleGroupDragOver = (e, groupId) => {
        e.preventDefault();
        setDragOverGroup(groupId);
    };

    const handleGroupDragLeave = () => {
        setDragOverGroup(null);
    };

    const handleGroupDrop = async (e, toGroupId) => {
        e.preventDefault();
        setDragOverGroup(null);

        // Handle external drag from sidebar (via dataTransfer)
        if (!draggedModule) {
            const moduleCode = e.dataTransfer.getData('moduleCode');
            const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');
            const moduleIdFromTransfer = e.dataTransfer.getData('moduleId');

            console.log('[Preferences] External drop:', { moduleCode, sourceGroupId, moduleIdFromTransfer, toGroupId });

            if (moduleCode) {
                const numericModuleId = moduleIdFromTransfer ? parseInt(moduleIdFromTransfer) : MODULE_ID_MAP[moduleCode];
                console.log('[Preferences] numericModuleId:', numericModuleId, 'from MODULE_ID_MAP:', MODULE_ID_MAP[moduleCode]);

                if (numericModuleId) {
                    // Don't do anything if dropping on same group
                    if (sourceGroupId && parseInt(sourceGroupId) === toGroupId) {
                        console.log('[Preferences] Same group, aborting');
                        return;
                    }

                    try {
                        // If coming from another group, remove from source first
                        if (sourceGroupId) {
                            console.log('[Preferences] Removing from source group:', sourceGroupId);
                            const removeResult = await removeModuleFromGroup(parseInt(sourceGroupId), numericModuleId);
                            console.log('[Preferences] Remove result:', removeResult);
                        }

                        // Add to target group
                        if (toGroupId) {
                            console.log('[Preferences] Adding to group:', toGroupId, 'moduleId:', numericModuleId);
                            const addResult = await addModuleToGroup(toGroupId, numericModuleId);
                            console.log('[Preferences] Add result:', addResult);
                        }

                        await loadModulesData();
                    } catch (error) {
                        console.error('Error moving module from sidebar:', error);
                    }
                } else {
                    console.log('[Preferences] No numericModuleId found, aborting');
                }
            } else {
                console.log('[Preferences] No moduleCode, aborting');
            }
            return;
        }

        const moduleId = draggedModule.id || draggedModule.moduleid;
        const fromGroupId = draggedModule.fromGroupId;

        console.log('Drop on group - draggedModule:', draggedModule);
        console.log('Drop on group - moduleId:', moduleId, 'fromGroupId:', fromGroupId, 'toGroupId:', toGroupId);

        // If dropping to same group, ignore
        if (fromGroupId === toGroupId) {
            console.log('Same group, ignoring');
            setDraggedModule(null);
            return;
        }

        // Remove from old group if needed
        if (fromGroupId) {
            console.log('Removing from group:', fromGroupId);
            await removeModuleFromGroup(fromGroupId, moduleId);
        }

        // Add to new group if not dropping to ungrouped
        if (toGroupId) {
            console.log('Adding to group:', toGroupId, 'moduleId:', moduleId);
            try {
                const result = await addModuleToGroup(toGroupId, moduleId);
                console.log('addModuleToGroup result:', result);
                if (!result.success && !result.SUCCESS) {
                    console.error('addModuleToGroup failed:', result.message || result.MESSAGE);
                }
            } catch (err) {
                console.error('addModuleToGroup error:', err);
            }
        }

        await loadModulesData();
        setDraggedModule(null);
    };

    // Module icon mapping
    const getModuleIcon = (code) => {
        const icons = {
            'CONTACTS': Users,
            'CONVERSATIONS': MessageCircle,
            'MAP': Map,
            'AGENDA': Calendar,
            'EVENTS': PartyPopper,
            'PLACES': Building2,
            'SUPPORT': HeadphonesIcon,
            'WORK': Briefcase,
            'OFFERS': Coins,
            'PRODUCTS': Package,
            'ADVERTISEMENTS': ImageIcon,
            'WEBSITES': Globe
        };
        return icons[code] || LayoutGrid;
    };

    const loadCommunityThemes = async () => {
        setLoadingCommunity(true);
        try {
            const [community, mine] = await Promise.all([
                getCommunityThemes(),
                getMyThemes()
            ]);
            setCommunityThemes(community);
            setMyCustomThemes(mine);
        } catch (error) {
            console.error('Error loading community themes:', error);
        } finally {
            setLoadingCommunity(false);
        }
    };

    // Toggle module visibility
    const handleToggleModule = (moduleKey) => {
        const newModules = { ...modules, [moduleKey]: !modules[moduleKey] };
        setModules(newModules);
        localStorage.setItem('cabazaModules', JSON.stringify(newModules));
        // Dispatch event to notify other components
        window.dispatchEvent(new Event('modulesChanged'));
    };

    // Debug: log themes
    console.log('All themes:', themes);
    console.log('Theme types:', themes.map(t => t.themeType));

    // Filter themes by type
    // Show all non-custom themes in the Cabaza tab
    const gradientThemes = themes.filter(t => t.themeType === 'gradient');
    const flatLightThemes = themes.filter(t => t.themeType === 'flat-light');
    const flatDarkThemes = themes.filter(t => t.themeType === 'flat-dark');
    const imageBgThemes = themes.filter(t => t.themeType === 'image-bg');
    const customThemes = themes.filter(t => t.themeType === 'custom');

    console.log('Gradient themes:', gradientThemes.length);
    console.log('Flat light themes:', flatLightThemes.length);
    console.log('Flat dark themes:', flatDarkThemes.length);
    console.log('Image themes:', imageBgThemes.length);

    // Further categorize themes by name patterns
    const macOsThemes = flatLightThemes.filter(t => t.name?.toLowerCase().includes('macos'));
    const windowsThemes = flatLightThemes.filter(t => t.name?.toLowerCase().includes('windows'));
    const macOsDarkThemes = flatDarkThemes.filter(t => t.name?.toLowerCase().includes('macos'));
    const windowsDarkThemes = flatDarkThemes.filter(t => t.name?.toLowerCase().includes('windows'));
    const otherLightThemes = flatLightThemes.filter(t =>
        !t.name?.toLowerCase().includes('macos') &&
        !t.name?.toLowerCase().includes('windows')
    );
    const otherDarkThemes = flatDarkThemes.filter(t =>
        !t.name?.toLowerCase().includes('macos') &&
        !t.name?.toLowerCase().includes('windows')
    );

    // Categorize image themes
    const skyThemes = imageBgThemes.filter(t => t.name?.toLowerCase().includes('sky') || t.name?.toLowerCase().includes('aurora'));
    const flowerThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('rose') ||
        t.name?.toLowerCase().includes('lavender') ||
        t.name?.toLowerCase().includes('sakura') ||
        t.name?.toLowerCase().includes('sunflower') ||
        t.name?.toLowerCase().includes('bloom')
    );
    const spaceThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('galaxy') ||
        t.name?.toLowerCase().includes('nebula') ||
        t.name?.toLowerCase().includes('star') ||
        t.name?.toLowerCase().includes('aurora borealis')
    );
    const animeThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('neon') ||
        t.name?.toLowerCase().includes('pastel') ||
        t.name?.toLowerCase().includes('cyberpunk') ||
        t.name?.toLowerCase().includes('ghibli')
    );
    const natureThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('forest') ||
        t.name?.toLowerCase().includes('ocean') ||
        t.name?.toLowerCase().includes('mountain') ||
        t.name?.toLowerCase().includes('desert') ||
        t.name?.toLowerCase().includes('beach')
    );
    const cityThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('city') ||
        t.name?.toLowerCase().includes('rainy')
    );
    const artThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('abstract') ||
        t.name?.toLowerCase().includes('geometric') ||
        t.name?.toLowerCase().includes('marble') ||
        t.name?.toLowerCase().includes('watercolor')
    );
    const seasonThemes = imageBgThemes.filter(t =>
        t.name?.toLowerCase().includes('autumn') ||
        t.name?.toLowerCase().includes('winter') ||
        t.name?.toLowerCase().includes('spring') ||
        t.name?.toLowerCase().includes('summer')
    );

    // Handle theme selection
    const handleSelectTheme = async (theme) => {
        setSelectedThemeId(theme.id);
        setSaving(true);
        try {
            await changeTheme(theme);
            setMessage({ type: 'success', text: t.preferences?.themeSaved || 'Theme applied!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        } catch (error) {
            setMessage({ type: 'error', text: t.preferences?.errorSaving || 'Error saving theme' });
        } finally {
            setSaving(false);
        }
    };

    // Handle custom color change
    const handleColorChange = (colorKey, value) => {
        setCustomColors(prev => ({ ...prev, [colorKey]: value }));
    };

    // Handle image upload
    const handleImageUpload = async (e) => {
        console.log('handleImageUpload called', e.target.files);
        const file = e.target.files?.[0];
        if (!file) {
            console.log('No file selected');
            return;
        }

        console.log('File selected:', file.name, file.type);

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: t.preferences?.invalidImageUrl || 'Invalid image' });
            return;
        }

        setUploading(true);
        try {
            console.log('Calling uploadThemeImage...');
            const result = await uploadThemeImage(file);
            console.log('Upload result:', result);
            if (result.success && result.imageUrl) {
                setUploadedImageUrl(result.imageUrl);
                setImageSource('upload');
                setMessage({ type: 'success', text: t.preferences?.themeSaved || 'Image uploaded!' });
            } else {
                setMessage({ type: 'error', text: result.message || t.preferences?.uploadError || 'Upload error' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: t.preferences?.uploadError || 'Upload error' });
        } finally {
            setUploading(false);
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        }
    };

    // Get final image URL based on source
    const getFinalImageUrl = () => {
        if (imageSource === 'upload') return uploadedImageUrl;
        if (imageSource === 'url') return imageUrl;
        return '';
    };

    // Apply custom colors live
    const handleApplyCustom = async () => {
        setSaving(true);
        try {
            const themeData = {
                ...customColors,
                sidebarBgImage: getFinalImageUrl(),
                sidebarBgOverlay: overlayColor
            };
            await applyCustomTheme(themeData);
            setSelectedThemeId(0);
            setMessage({ type: 'success', text: t.preferences?.customApplied || 'Custom theme applied!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 2000);
        } catch (error) {
            setMessage({ type: 'error', text: t.preferences?.errorApplying || 'Error applying theme' });
        } finally {
            setSaving(false);
        }
    };

    // Save custom theme to database
    const handleSaveCustomTheme = async () => {
        const themeName = customThemeName.trim() || customThemeNameFr.trim() || customThemeNameEn.trim();
        if (!themeName) {
            setMessage({ type: 'error', text: t.preferences?.nameRequired || 'Please enter a theme name' });
            return;
        }

        setSaving(true);
        try {
            const result = await createTheme({
                name: themeName,
                nameFr: customThemeNameFr.trim() || themeName,
                nameEn: customThemeNameEn.trim() || themeName,
                themeType: getFinalImageUrl() ? 'image-bg' : 'custom',
                ...customColors,
                sidebarBgImage: getFinalImageUrl(),
                sidebarBgOverlay: overlayColor,
                imageSource: imageSource !== 'none' ? imageSource : null,
                visibility: visibility,
                createdById: userId
            });

            if (result.success) {
                await refreshThemes();
                // Reset form
                setCustomThemeName('');
                setCustomThemeNameFr('');
                setCustomThemeNameEn('');
                setImageSource('none');
                setImageUrl('');
                setUploadedImageUrl('');
                const savedVisibility = visibility;
                setVisibility('private');
                setMessage({ type: 'success', text: t.preferences?.themeSavedToLibrary || 'Theme saved to your library!' });
                setTimeout(() => setMessage({ type: '', text: '' }), 2000);
                // Redirect based on visibility
                if (savedVisibility === 'public') {
                    setThemeSubTab('community');
                    loadCommunityThemes();
                } else {
                    setThemeSubTab('mythemes');
                    loadCommunityThemes();
                }
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: t.preferences?.errorSaving || 'Error saving theme' });
        } finally {
            setSaving(false);
        }
    };

    // Delete custom theme
    const handleDeleteTheme = async (themeId) => {
        setSaving(true);
        try {
            const result = await deleteTheme(themeId);
            if (result.success) {
                await refreshThemes();
                await loadCommunityThemes(); // Refresh local lists
                if (selectedThemeId === themeId) {
                    await resetToDefault();
                }
                setMessage({ type: 'success', text: t.preferences?.themeDeleted || 'Theme deleted' });
                setTimeout(() => setMessage({ type: '', text: '' }), 2000);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: t.preferences?.errorDeleting || 'Error deleting theme' });
        } finally {
            setSaving(false);
            setShowDeleteConfirm(null);
        }
    };

    // Handle like
    const handleLikeTheme = async (themeId) => {
        try {
            const result = await likeTheme(themeId);
            if (result.success) {
                // Refresh community themes to update like counts
                loadCommunityThemes();
            }
        } catch (error) {
            console.error('Error liking theme:', error);
        }
    };

    // Theme card component
    const ThemeCard = ({ theme, isSelected, onSelect, showDelete = false, showStats = false }) => {
        const hasImageBg = theme.sidebarBgImage;

        return (
            <div
                className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                    isSelected
                        ? 'border-[var(--color-cabaza-purple)] shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => onSelect(theme)}
            >
                {/* Theme preview */}
                <div
                    className="h-20 rounded-lg mb-3 flex items-center justify-center overflow-hidden relative"
                    style={hasImageBg ? {
                        backgroundImage: `url(${theme.sidebarBgImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    } : {
                        background: theme.secondaryColor
                            ? `linear-gradient(135deg, ${theme.primaryColor} 0%, ${theme.secondaryColor} 100%)`
                            : theme.primaryColor
                    }}
                >
                    {hasImageBg && (
                        <div
                            className="absolute inset-0"
                            style={{ backgroundColor: theme.sidebarBgOverlay || 'rgba(0,0,0,0.3)' }}
                        />
                    )}
                    <div
                        className="w-12 h-8 rounded shadow-sm relative z-10"
                        style={{ backgroundColor: theme.surfaceColor }}
                    />
                </div>

                {/* Theme name */}
                <p className="font-medium text-gray-900 text-sm truncate">
                    {getThemeName(theme, language)}
                </p>

                {/* Type badge and stats */}
                <div className="flex items-center gap-1 mt-1">
                    {theme.themeType === 'gradient' && <Sparkles className="w-3 h-3 text-purple-500" />}
                    {theme.themeType === 'flat-light' && <Sun className="w-3 h-3 text-amber-500" />}
                    {theme.themeType === 'flat-dark' && <Moon className="w-3 h-3 text-indigo-500" />}
                    {theme.themeType === 'image-bg' && <ImageIcon className="w-3 h-3 text-pink-500" />}
                    {theme.themeType === 'custom' && <Palette className="w-3 h-3 text-teal-500" />}
                    {theme.name?.toLowerCase().includes('macos') && <Apple className="w-3 h-3 text-gray-500" />}
                    {theme.name?.toLowerCase().includes('windows') && <Monitor className="w-3 h-3 text-blue-500" />}
                    <span className="text-xs text-gray-500 capitalize">
                        {theme.themeType === 'image-bg' ? 'visual' : theme.themeType?.replace('-', ' ')}
                    </span>
                </div>

                {/* Stats for community themes */}
                {showStats && (
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {theme.likesCount || 0}
                        </span>
                        <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {theme.downloadsCount || 0}
                        </span>
                    </div>
                )}

                {/* Visibility badge */}
                {theme.visibility && !theme.isSystem && (
                    <div className="absolute top-2 left-2">
                        {theme.visibility === 'public' ? (
                            <Globe className="w-4 h-4 text-green-500" />
                        ) : (
                            <Lock className="w-4 h-4 text-gray-400" />
                        )}
                    </div>
                )}

                {/* Selected indicator */}
                {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-[var(--color-cabaza-purple)] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                    </div>
                )}

                {/* Delete button for custom themes */}
                {showDelete && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteConfirm(theme.id);
                        }}
                        className="absolute bottom-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        );
    };

    // Color picker input
    const ColorInput = ({ label, colorKey, value }) => (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => handleColorChange(colorKey, e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => handleColorChange(colorKey, e.target.value)}
                    className="w-20 px-2 py-1 text-xs font-mono border border-gray-300 rounded-lg"
                />
            </div>
        </div>
    );

    // Theme section component
    const ThemeSection = ({ title, icon: Icon, themes: sectionThemes, bgClass = "bg-white" }) => {
        if (!sectionThemes || sectionThemes.length === 0) return null;

        return (
            <div className={`${bgClass} rounded-2xl shadow-sm p-6`}>
                <div className="flex items-center gap-2 mb-4">
                    {typeof Icon === 'string' ? (
                        <span className="text-2xl">{Icon}</span>
                    ) : (
                        <Icon className="w-5 h-5" />
                    )}
                    <h2 className={`text-lg font-semibold ${bgClass.includes('indigo-900') || bgClass.includes('purple-900') ? 'text-white' : 'text-gray-900'}`}>
                        {title}
                    </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {sectionThemes.map(theme => (
                        <ThemeCard
                            key={theme.id}
                            theme={theme}
                            isSelected={selectedThemeId === theme.id}
                            onSelect={handleSelectTheme}
                        />
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Settings className="w-8 h-8 text-[var(--color-cabaza-purple)]" />
                        <h1 className="text-2xl font-bold text-gray-900">
                            {t.preferences?.title || 'Preferences'}
                        </h1>
                    </div>
                    <p className="text-gray-600">
                        {t.preferences?.subtitle || 'Customize your Cabaza experience'}
                    </p>
                </div>

                {/* Message */}
                {message.text && (
                    <div className={`mb-6 p-4 rounded-lg ${
                        message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                        message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                        'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                        {message.text}
                    </div>
                )}

                {/* Main Tabs */}
                <div className="flex gap-1 mb-4 border-b border-gray-200">
                    <button
                        onClick={() => setMainTab('themes')}
                        className={`px-3 sm:px-6 py-3 font-medium transition-colors flex items-center justify-center sm:justify-start gap-2 border-b-2 -mb-px ${
                            mainTab === 'themes'
                                ? 'border-[var(--color-cabaza-purple)] text-[var(--color-cabaza-purple)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Palette className="w-5 h-5" />
                        <span className="hidden sm:inline">{t.preferences?.themesTab || 'Thèmes'}</span>
                    </button>
                    <button
                        onClick={() => setMainTab('modules')}
                        className={`px-3 sm:px-6 py-3 font-medium transition-colors flex items-center justify-center sm:justify-start gap-2 border-b-2 -mb-px ${
                            mainTab === 'modules'
                                ? 'border-[var(--color-cabaza-purple)] text-[var(--color-cabaza-purple)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <LayoutGrid className="w-5 h-5" />
                        <span className="hidden sm:inline">{t.preferences?.modulesTab || 'Modules'}</span>
                    </button>
                    <button
                        onClick={() => setMainTab('languages')}
                        className={`px-3 sm:px-6 py-3 font-medium transition-colors flex items-center justify-center sm:justify-start gap-2 border-b-2 -mb-px ${
                            mainTab === 'languages'
                                ? 'border-[var(--color-cabaza-purple)] text-[var(--color-cabaza-purple)]'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Globe className="w-5 h-5" />
                        <span className="hidden sm:inline">{t.preferences?.languagesTab || 'Langues'}</span>
                    </button>
                </div>

                {/* Theme Sub-Tabs */}
                {mainTab === 'themes' && (
                    <div className="flex gap-2 mb-6 flex-wrap">
                        <button
                            onClick={() => setThemeSubTab('cabaza')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                themeSubTab === 'cabaza'
                                    ? 'bg-[var(--color-cabaza-purple)] text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                        >
                            <Palette className="w-4 h-4" />
                            {t.preferences?.cabazaThemes || 'Thèmes Cabaza'}
                        </button>
                        <button
                            onClick={() => setThemeSubTab('community')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                themeSubTab === 'community'
                                    ? 'bg-[var(--color-cabaza-purple)] text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                        >
                            <Users className="w-4 h-4" />
                            {t.preferences?.communityTab || 'Communauté'}
                        </button>
                        <button
                            onClick={() => setThemeSubTab('mythemes')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                themeSubTab === 'mythemes'
                                    ? 'bg-[var(--color-cabaza-purple)] text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                        >
                            <Lock className="w-4 h-4" />
                            {t.preferences?.myPrivateThemes || 'Mes thèmes privés'}
                        </button>
                        <button
                            onClick={() => setThemeSubTab('custom')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                                themeSubTab === 'custom'
                                    ? 'bg-[var(--color-cabaza-purple)] text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            }`}
                        >
                            <Plus className="w-4 h-4" />
                            {t.preferences?.customTab || 'Créer un thème'}
                        </button>
                    </div>
                )}

                {/* Cabaza Themes Tab */}
                {mainTab === 'themes' && themeSubTab === 'cabaza' && (
                    <div className="space-y-8">
                        {/* Header for Cabaza section */}
                        <div className="flex items-center gap-2 text-gray-700">
                            <Sparkles className="w-5 h-5 text-[var(--color-cabaza-purple)]" />
                            <span className="font-medium">{t.preferences?.cabazaThemes || 'Official Cabaza Themes'}</span>
                        </div>

                        {/* Gradient Themes */}
                        <ThemeSection
                            title={t.preferences?.gradientThemes || 'Gradient Themes'}
                            icon={Sparkles}
                            themes={gradientThemes}
                        />

                        {/* macOS Themes */}
                        {(macOsThemes.length > 0 || macOsDarkThemes.length > 0) && (
                            <ThemeSection
                                title={t.preferences?.macosThemes || 'macOS'}
                                icon={Apple}
                                themes={[...macOsThemes, ...macOsDarkThemes]}
                            />
                        )}

                        {/* Windows Themes */}
                        {(windowsThemes.length > 0 || windowsDarkThemes.length > 0) && (
                            <ThemeSection
                                title={t.preferences?.windowsThemes || 'Windows'}
                                icon={Monitor}
                                themes={[...windowsThemes, ...windowsDarkThemes]}
                            />
                        )}

                        {/* Other Light Themes */}
                        <ThemeSection
                            title={t.preferences?.lightThemes || 'Light Themes'}
                            icon={Sun}
                            themes={otherLightThemes}
                        />

                        {/* Dark Themes */}
                        <ThemeSection
                            title={t.preferences?.darkThemes || 'Dark Themes'}
                            icon={Moon}
                            themes={otherDarkThemes}
                        />

                        {/* === VISUAL THEMES WITH IMAGES === */}
                        {imageBgThemes.length > 0 && (
                            <>
                                <div className="border-t pt-8 mt-8">
                                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                        <ImageIcon className="w-6 h-6 text-pink-500" />
                                        {t.preferences?.visualThemes || 'Visual Themes'}
                                    </h2>
                                </div>

                                <ThemeSection title={t.preferences?.skyThemes || 'Sky'} icon="🌤️" themes={skyThemes} bgClass="bg-gradient-to-br from-sky-50 to-blue-50" />
                                <ThemeSection title={t.preferences?.flowerThemes || 'Flowers'} icon="🌸" themes={flowerThemes} bgClass="bg-gradient-to-br from-pink-50 to-rose-50" />
                                <ThemeSection title={t.preferences?.spaceThemes || 'Space'} icon="🌌" themes={spaceThemes} bgClass="bg-gradient-to-br from-indigo-900 to-purple-900" />
                                <ThemeSection title={t.preferences?.animeThemes || 'Anime & Manga'} icon="🎌" themes={animeThemes} bgClass="bg-gradient-to-br from-fuchsia-100 to-cyan-100" />
                                <ThemeSection title={t.preferences?.natureThemes || 'Nature'} icon={Leaf} themes={natureThemes} bgClass="bg-gradient-to-br from-green-50 to-emerald-50" />
                                <ThemeSection title={t.preferences?.cityThemes || 'City'} icon={Building2} themes={cityThemes} bgClass="bg-gradient-to-br from-slate-100 to-gray-200" />
                                <ThemeSection title={t.preferences?.artThemes || 'Art & Abstract'} icon={Palette} themes={artThemes} bgClass="bg-gradient-to-br from-violet-50 to-fuchsia-50" />
                                <ThemeSection title={t.preferences?.seasonThemes || 'Seasons'} icon={Mountain} themes={seasonThemes} bgClass="bg-gradient-to-br from-amber-50 to-orange-50" />
                            </>
                        )}

                        {/* Reset button */}
                        <div className="flex justify-center">
                            <button
                                onClick={resetToDefault}
                                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t.preferences?.resetToDefault || 'Reset to default'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Community Themes Tab */}
                {mainTab === 'themes' && themeSubTab === 'community' && (
                    <div className="space-y-8">
                        {loadingCommunity ? (
                            <div className="text-center py-12">
                                <div className="animate-spin w-8 h-8 border-4 border-[var(--color-cabaza-purple)] border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-gray-600">{t.common?.loading || 'Loading...'}</p>
                            </div>
                        ) : (
                            <>
                                {/* My Public Themes */}
                                {myCustomThemes.filter(t => t.visibility === 'public').length > 0 && (
                                    <div className="bg-white rounded-2xl shadow-sm p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Globe className="w-5 h-5 text-green-500" />
                                            <h2 className="text-lg font-semibold text-gray-900">
                                                {t.preferences?.myPublicThemes || 'My Public Themes'}
                                            </h2>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {myCustomThemes.filter(t => t.visibility === 'public').map(theme => (
                                                <ThemeCard
                                                    key={theme.id}
                                                    theme={theme}
                                                    isSelected={selectedThemeId === theme.id}
                                                    onSelect={handleSelectTheme}
                                                    showDelete
                                                    showStats
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Community Themes (from other users) */}
                                <div className="bg-white rounded-2xl shadow-sm p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Users className="w-5 h-5 text-blue-500" />
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            {t.preferences?.communityThemes || 'Community Themes'}
                                        </h2>
                                    </div>
                                    {communityThemes.filter(t => t.createdById !== userId).length > 0 ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {communityThemes.filter(t => t.createdById !== userId).map(theme => (
                                                <div key={theme.id} className="relative">
                                                    <ThemeCard
                                                        theme={theme}
                                                        isSelected={selectedThemeId === theme.id}
                                                        onSelect={handleSelectTheme}
                                                        showStats
                                                    />
                                                    {/* Like button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleLikeTheme(theme.id);
                                                        }}
                                                        className="absolute bottom-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <Heart className="w-4 h-4" />
                                                    </button>
                                                    {/* Creator info */}
                                                    {theme.creatorName && (
                                                        <p className="text-xs text-gray-400 mt-1 px-4">
                                                            {t.preferences?.createdBy || 'by'} {theme.creatorName}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                            <p>{t.preferences?.noCommunityThemes || 'No community themes available'}</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* My Private Themes Tab */}
                {mainTab === 'themes' && themeSubTab === 'mythemes' && (
                    <div className="space-y-8">
                        {loadingCommunity ? (
                            <div className="text-center py-12">
                                <div className="animate-spin w-8 h-8 border-4 border-[var(--color-cabaza-purple)] border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-gray-600">{t.common?.loading || 'Loading...'}</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl shadow-sm p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Lock className="w-5 h-5 text-gray-500" />
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {t.preferences?.myPrivateThemes || 'My Private Themes'}
                                    </h2>
                                </div>
                                {myCustomThemes.filter(t => t.visibility === 'private').length > 0 ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {myCustomThemes.filter(t => t.visibility === 'private').map(theme => (
                                            <ThemeCard
                                                key={theme.id}
                                                theme={theme}
                                                isSelected={selectedThemeId === theme.id}
                                                onSelect={handleSelectTheme}
                                                showDelete
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <Lock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                        <p>{t.preferences?.noPrivateThemes || 'No private themes yet'}</p>
                                        <Button onClick={() => setThemeSubTab('custom')} className="mt-4">
                                            {t.preferences?.createCustomTheme || 'Create a theme'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Custom Theme Tab */}
                {mainTab === 'themes' && themeSubTab === 'custom' && (
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6">
                            {t.preferences?.createCustomTheme || 'Create Your Theme'}
                        </h2>

                        {/* Live Preview */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                {t.preferences?.preview || 'Preview'}
                            </h3>
                            <div
                                className="p-6 rounded-xl border relative overflow-hidden"
                                style={{
                                    backgroundColor: customColors.backgroundColor,
                                    borderColor: customColors.borderColor
                                }}
                            >
                                {/* Background image preview */}
                                {getFinalImageUrl() && (
                                    <>
                                        <div
                                            className="absolute inset-0 bg-cover bg-center"
                                            style={{ backgroundImage: `url(${getFinalImageUrl()})` }}
                                        />
                                        <div
                                            className="absolute inset-0"
                                            style={{ backgroundColor: overlayColor }}
                                        />
                                    </>
                                )}

                                <div className="relative z-10">
                                    <div
                                        className="h-12 rounded-lg mb-4 flex items-center justify-center"
                                        style={{
                                            background: customColors.secondaryColor
                                                ? `linear-gradient(135deg, ${customColors.primaryColor} 0%, ${customColors.secondaryColor} 100%)`
                                                : customColors.primaryColor
                                        }}
                                    >
                                        <span style={{ color: '#ffffff' }} className="font-medium">
                                            {t.preferences?.headerPreview || 'Header'}
                                        </span>
                                    </div>
                                    <div
                                        className="p-4 rounded-lg"
                                        style={{ backgroundColor: customColors.surfaceColor }}
                                    >
                                        <p style={{ color: customColors.textPrimary }} className="font-medium">
                                            {t.preferences?.cardTitle || 'Card Title'}
                                        </p>
                                        <p style={{ color: customColors.textSecondary }} className="text-sm mt-1">
                                            {t.preferences?.cardDescription || 'This is how your content will look.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Image Source Selection */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                {t.preferences?.imageSource || 'Image Source'}
                            </h3>
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <button
                                    onClick={() => setImageSource('none')}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                        imageSource === 'none'
                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <EyeOff className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                                    <span className="text-xs">{t.preferences?.noImage || 'No image'}</span>
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                        imageSource === 'upload'
                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Upload className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                                    <span className="text-xs">{t.preferences?.uploadImage || 'Upload'}</span>
                                </button>
                                <button
                                    onClick={() => setImageSource('url')}
                                    className={`p-3 rounded-lg border-2 transition-all ${
                                        imageSource === 'url'
                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Link className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                                    <span className="text-xs">{t.preferences?.externalUrl || 'URL'}</span>
                                </button>
                            </div>

                            {/* Hidden file input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                            />

                            {/* URL input */}
                            {imageSource === 'url' && (
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder={t.preferences?.imageUrlPlaceholder || 'https://example.com/image.jpg'}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[var(--color-cabaza-purple)]"
                                />
                            )}

                            {/* Upload status */}
                            {uploading && (
                                <div className="flex items-center gap-2 text-gray-500 mt-2">
                                    <div className="animate-spin w-4 h-4 border-2 border-[var(--color-cabaza-purple)] border-t-transparent rounded-full"></div>
                                    <span>{t.preferences?.uploading || 'Uploading...'}</span>
                                </div>
                            )}

                            {/* Uploaded image preview */}
                            {imageSource === 'upload' && uploadedImageUrl && (
                                <div className="mt-2 text-sm text-green-600 flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    {t.preferences?.themeSaved || 'Image uploaded!'}
                                </div>
                            )}

                            {/* Overlay color for images */}
                            {getFinalImageUrl() && (
                                <div className="mt-4">
                                    <label className="text-sm font-medium text-gray-700 block mb-2">
                                        {t.preferences?.overlayColor || 'Overlay color'}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t.preferences?.overlayColorDescription || 'Darkens the image for better text readability'}
                                    </p>
                                    <div className="flex gap-2">
                                        {['rgba(0,0,0,0.3)', 'rgba(0,0,0,0.5)', 'rgba(0,0,0,0.7)', 'rgba(0,0,50,0.5)', 'rgba(50,0,50,0.5)'].map(color => (
                                            <button
                                                key={color}
                                                onClick={() => setOverlayColor(color)}
                                                className={`w-8 h-8 rounded-lg border-2 ${
                                                    overlayColor === color ? 'border-[var(--color-cabaza-purple)]' : 'border-gray-200'
                                                }`}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Color Pickers */}
                        <div className="space-y-3 mb-6">
                            <ColorInput
                                label={t.preferences?.primaryColor || 'Primary Color'}
                                colorKey="primaryColor"
                                value={customColors.primaryColor}
                            />
                            <ColorInput
                                label={t.preferences?.secondaryColor || 'Secondary Color (Gradient)'}
                                colorKey="secondaryColor"
                                value={customColors.secondaryColor}
                            />
                            <ColorInput
                                label={t.preferences?.backgroundColor || 'Background'}
                                colorKey="backgroundColor"
                                value={customColors.backgroundColor}
                            />
                            <ColorInput
                                label={t.preferences?.surfaceColor || 'Surface (Cards)'}
                                colorKey="surfaceColor"
                                value={customColors.surfaceColor}
                            />
                            <ColorInput
                                label={t.preferences?.textPrimaryColor || 'Primary Text'}
                                colorKey="textPrimary"
                                value={customColors.textPrimary}
                            />
                            <ColorInput
                                label={t.preferences?.textSecondaryColor || 'Secondary Text'}
                                colorKey="textSecondary"
                                value={customColors.textSecondary}
                            />
                            <ColorInput
                                label={t.preferences?.borderColor || 'Borders'}
                                colorKey="borderColor"
                                value={customColors.borderColor}
                            />
                        </div>

                        {/* Visibility Selection */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">
                                {t.preferences?.visibility || 'Visibility'}
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setVisibility('private')}
                                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                                        visibility === 'private'
                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Lock className="w-5 h-5 mb-2 text-gray-600" />
                                    <p className="font-medium text-gray-900">{t.preferences?.private || 'Private'}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t.preferences?.privateDescription || 'Only you can see and use this theme'}
                                    </p>
                                </button>
                                <button
                                    onClick={() => setVisibility('public')}
                                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                                        visibility === 'public'
                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <Globe className="w-5 h-5 mb-2 text-green-600" />
                                    <p className="font-medium text-gray-900">{t.preferences?.public || 'Public'}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t.preferences?.publicDescription || 'Your theme will be visible to all users'}
                                    </p>
                                </button>
                            </div>
                        </div>

                        {/* Theme Name Inputs */}
                        <div className="mb-6 space-y-3">
                            <div>
                                <label className="text-sm font-medium text-gray-700 block mb-1">
                                    {language === 'fr' ? 'Nom du theme (FR)' : 'Theme name (FR)'}
                                </label>
                                <input
                                    type="text"
                                    value={customThemeNameFr}
                                    onChange={(e) => setCustomThemeNameFr(e.target.value)}
                                    placeholder={language === 'fr' ? 'Mon theme personnalise...' : 'My custom theme...'}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[var(--color-cabaza-purple)]"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 block mb-1">
                                    {language === 'fr' ? 'Nom du theme (EN)' : 'Theme name (EN)'}
                                </label>
                                <input
                                    type="text"
                                    value={customThemeNameEn}
                                    onChange={(e) => setCustomThemeNameEn(e.target.value)}
                                    placeholder={language === 'fr' ? 'My custom theme...' : 'My custom theme...'}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[var(--color-cabaza-purple)]"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button onClick={handleApplyCustom} loading={saving} icon={Check} className="flex-1">
                                {t.preferences?.applyTheme || 'Apply Theme'}
                            </Button>
                            <Button
                                onClick={handleSaveCustomTheme}
                                disabled={!customThemeNameFr.trim() && !customThemeNameEn.trim()}
                                loading={saving}
                                variant="secondary"
                                icon={Save}
                                className="flex-1"
                            >
                                {t.preferences?.save || 'Save'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Modules Tab */}
                {mainTab === 'modules' && (
                    <div className="space-y-6">
                        {/* Sidebar Toggle Section */}
                        <div className="bg-white rounded-2xl shadow-sm p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {t.preferences?.sidebarEnabled || 'Sidebar'}
                                    </h2>
                                    <p className="text-gray-600 text-sm">
                                        {t.preferences?.sidebarEnabledDesc || 'Afficher la barre latérale de navigation'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        const newValue = localStorage.getItem('cabaza_sidebar_enabled') !== 'true';
                                        localStorage.setItem('cabaza_sidebar_enabled', newValue.toString());
                                        window.location.reload();
                                    }}
                                    className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                                        localStorage.getItem('cabaza_sidebar_enabled') === 'true'
                                            ? 'bg-[var(--color-cabaza-purple)]'
                                            : 'bg-gray-300'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out mt-1 ${
                                            localStorage.getItem('cabaza_sidebar_enabled') === 'true'
                                                ? 'ml-7'
                                                : 'ml-1'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {loadingModules ? (
                            <div className="text-center py-12">
                                <div className="animate-spin w-8 h-8 border-4 border-[var(--color-cabaza-purple)] border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-gray-600">{t.common?.loading || 'Loading...'}</p>
                            </div>
                        ) : (
                            <>
                                {/* Module Visibility Section */}
                                <div className="bg-white rounded-2xl shadow-sm p-6">
                                    <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                        {t.preferences?.modulesVisibility || 'Module Visibility'}
                                    </h2>
                                    <p className="text-gray-600 mb-4 text-sm">
                                        {t.preferences?.modulesVisibilityDesc || 'Enable or disable modules'}
                                    </p>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                                        {allModules.map((mod) => {
                                            const moduleKey = MODULE_NAV_MAP[mod.code] || mod.code.toLowerCase();
                                            const isEnabled = modules[moduleKey] !== false;
                                            const Icon = getModuleIcon(mod.code);

                                            return (
                                                <div
                                                    key={mod.id}
                                                    onClick={() => handleToggleModule(moduleKey)}
                                                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                                        isEnabled
                                                            ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                                            : 'border-gray-200 hover:border-gray-300 opacity-50'
                                                    }`}
                                                >
                                                    <div
                                                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                                                        style={{ backgroundColor: isEnabled ? (mod.color || '#8b5cf6') : '#e5e7eb' }}
                                                    >
                                                        <Icon className="w-5 h-5 text-white" />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900 text-center">
                                                        {mod[`name_${language}`] || mod.name_fr}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Groups Section */}
                                <div className="bg-white rounded-2xl shadow-sm p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900">
                                                {t.preferences?.moduleGroups || 'Module Groups'}
                                            </h2>
                                            <p className="text-gray-600 text-sm">
                                                {t.preferences?.moduleGroupsDesc || 'Organize modules into groups (max 5 groups, 12 modules each)'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Create new group */}
                                    {moduleGroups.length < 5 && (
                                        <div className="flex gap-2 mb-6">
                                            <input
                                                type="color"
                                                value={newGroupColor}
                                                onChange={(e) => setNewGroupColor(e.target.value)}
                                                className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200"
                                            />
                                            <input
                                                type="text"
                                                value={newGroupName}
                                                onChange={(e) => setNewGroupName(e.target.value)}
                                                placeholder={t.preferences?.newGroupPlaceholder || 'New group name...'}
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[var(--color-cabaza-purple)]"
                                                onKeyPress={(e) => e.key === 'Enter' && handleCreateGroup()}
                                            />
                                            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()} icon={Plus}>
                                                {t.preferences?.createGroup || 'Create'}
                                            </Button>
                                        </div>
                                    )}

                                    {/* Ungrouped modules */}
                                    <div
                                        className={`mb-6 p-4 rounded-xl border-2 border-dashed transition-all ${
                                            dragOverGroup === null ? 'border-gray-300 bg-gray-50' : 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                        }`}
                                        onDragOver={(e) => handleGroupDragOver(e, null)}
                                        onDragLeave={handleGroupDragLeave}
                                        onDrop={(e) => handleGroupDrop(e, null)}
                                    >
                                        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                            <LayoutGrid className="w-4 h-4" />
                                            {t.preferences?.ungroupedModules || 'Available Modules'}
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {getUngroupedModules().map((mod) => {
                                                const Icon = getModuleIcon(mod.code);
                                                return (
                                                    <div
                                                        key={mod.id}
                                                        draggable
                                                        onDragStart={(e) => handleModuleDragStart(e, mod, null)}
                                                        className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                                                    >
                                                        <GripVertical className="w-3 h-3 text-gray-400" />
                                                        <div
                                                            className="w-6 h-6 rounded flex items-center justify-center"
                                                            style={{ backgroundColor: mod.color || '#8b5cf6' }}
                                                        >
                                                            <Icon className="w-3 h-3 text-white" />
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {mod[`name_${language}`] || mod.name_fr}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            {getUngroupedModules().length === 0 && (
                                                <p className="text-sm text-gray-400 italic">
                                                    {t.preferences?.allModulesGrouped || 'All modules are in groups'}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Groups */}
                                    <div className="space-y-4">
                                        {moduleGroups.map((group) => (
                                            <div
                                                key={group.id}
                                                className={`rounded-xl border-2 transition-all ${
                                                    dragOverGroup === group.id ? 'border-[var(--color-cabaza-purple)] bg-purple-50' : 'border-gray-200'
                                                }`}
                                                onDragOver={(e) => handleGroupDragOver(e, group.id)}
                                                onDragLeave={handleGroupDragLeave}
                                                onDrop={(e) => handleGroupDrop(e, group.id)}
                                            >
                                                {/* Group header */}
                                                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                                                            style={{ backgroundColor: group.color || '#8b5cf6' }}
                                                        >
                                                            {group.iscollapsed ? (
                                                                <Folder className="w-4 h-4 text-white" />
                                                            ) : (
                                                                <FolderOpen className="w-4 h-4 text-white" />
                                                            )}
                                                        </div>
                                                        {editingGroup === group.id ? (
                                                            <input
                                                                type="text"
                                                                defaultValue={group.name}
                                                                autoFocus
                                                                className="px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-[var(--color-cabaza-purple)]"
                                                                onBlur={(e) => handleUpdateGroup(group.id, { name: e.target.value })}
                                                                onKeyPress={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        handleUpdateGroup(group.id, { name: e.target.value });
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="font-medium text-gray-900">{group.name}</span>
                                                        )}
                                                        <span className="text-xs text-gray-400">
                                                            ({group.modules?.length || 0}/12)
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleTogglePinGroup(group.id)}
                                                            className={`p-1.5 rounded-full transition-all ${
                                                                isGroupPinned(group.id)
                                                                    ? 'bg-cabaza-purple text-white'
                                                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                                                            }`}
                                                            title={isGroupPinned(group.id) ? 'Unpin from sidebar' : 'Pin to sidebar'}
                                                        >
                                                            <Pin className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingGroup(group.id)}
                                                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                                                            title={t.common?.edit || 'Edit'}
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteGroup(group.id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                                            title={t.common?.delete || 'Delete'}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Group modules */}
                                                {!group.iscollapsed && (
                                                    <div className="p-4">
                                                        <div className="flex flex-wrap gap-2 min-h-[40px]">
                                                            {group.modules?.map((mod) => {
                                                                const Icon = getModuleIcon(mod.code);
                                                                return (
                                                                    <div
                                                                        key={mod.itemid}
                                                                        draggable
                                                                        onDragStart={(e) => handleModuleDragStart(e, mod, group.id)}
                                                                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
                                                                    >
                                                                        <GripVertical className="w-3 h-3 text-gray-400" />
                                                                        <div
                                                                            className="w-6 h-6 rounded flex items-center justify-center"
                                                                            style={{ backgroundColor: mod.color || '#8b5cf6' }}
                                                                        >
                                                                            <Icon className="w-3 h-3 text-white" />
                                                                        </div>
                                                                        <span className="text-sm font-medium text-gray-700">
                                                                            {mod.name}
                                                                        </span>
                                                                        <button
                                                                            onClick={() => handleRemoveModuleFromGroup(group.id, mod.moduleid)}
                                                                            className="ml-1 p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                            {(!group.modules || group.modules.length === 0) && (
                                                                <p className="text-sm text-gray-400 italic">
                                                                    {t.preferences?.dropModulesHere || 'Drag modules here'}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {moduleGroups.length === 0 && (
                                        <div className="text-center py-8 text-gray-500">
                                            <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                            <p>{t.preferences?.noGroups || 'No groups yet'}</p>
                                            <p className="text-sm mt-1">{t.preferences?.createGroupHint || 'Create a group to organize your modules'}</p>
                                        </div>
                                    )}
                                </div>

                                <p className="text-xs text-gray-400 text-center">
                                    {t.preferences?.modulesNote || 'Changes are saved automatically'}
                                </p>
                            </>
                        )}
                    </div>
                )}

                {/* Languages Tab */}
                {mainTab === 'languages' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-sm p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <Globe className="w-6 h-6 text-[var(--color-cabaza-purple)]" />
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {t.preferences?.visibleLanguages || 'Langues visibles'}
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {t.preferences?.visibleLanguagesDesc || 'Choisissez les langues qui apparaissent dans le sélecteur de langue'}
                                    </p>
                                </div>
                            </div>

                            {/* Languages Grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {languages.map((lang) => {
                                    const isVisible = visibleLanguageCodes?.includes(lang.code);
                                    const isCurrentLang = lang.code === language;

                                    return (
                                        <div
                                            key={lang.code}
                                            onClick={() => {
                                                if (isCurrentLang) return; // Can't hide current language
                                                const newCodes = isVisible
                                                    ? visibleLanguageCodes.filter(c => c !== lang.code)
                                                    : [...visibleLanguageCodes, lang.code];
                                                setVisibleLanguageCodes(newCodes);
                                            }}
                                            className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                                isVisible
                                                    ? 'border-[var(--color-cabaza-purple)] bg-purple-50'
                                                    : 'border-gray-200 hover:border-gray-300 opacity-50'
                                            } ${isCurrentLang ? 'cursor-default' : ''}`}
                                        >
                                            <div className="flex flex-col items-center gap-2">
                                                <span className="text-2xl font-bold text-gray-400">{lang.flag}</span>
                                                <span className="text-sm font-medium text-gray-900">{lang.name}</span>
                                            </div>

                                            {/* Current language badge */}
                                            {isCurrentLang && (
                                                <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-[var(--color-cabaza-purple)] text-white text-[10px] font-medium rounded">
                                                    {t.preferences?.current || 'Actuelle'}
                                                </div>
                                            )}

                                            {/* Visibility indicator */}
                                            {isVisible && !isCurrentLang && (
                                                <div className="absolute top-2 right-2 w-5 h-5 bg-[var(--color-cabaza-purple)] rounded-full flex items-center justify-center">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Info */}
                            <p className="text-xs text-gray-400 mt-4">
                                {t.preferences?.languagesNote || 'La langue actuelle est toujours visible. Minimum 1 langue requise.'}
                            </p>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                {t.preferences?.deleteTheme || 'Delete Theme'}
                            </h3>
                            <p className="text-gray-600 mb-6">
                                {t.preferences?.deleteThemeConfirm || 'Are you sure you want to delete this theme?'}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    {t.common?.cancel || 'Cancel'}
                                </button>
                                <button
                                    onClick={() => handleDeleteTheme(showDeleteConfirm)}
                                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                >
                                    {t.common?.delete || 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Preferences;
